import {Injectable} from "@angular/core";
import {PromotionDetail} from "@app/core/promotion/domain/dtos/promotion-detail";
import {CheckStockUseCase} from "@app/core/stock/application/check-stock.use-case";
import {NoStockError} from "@app/core/stock/domain/errors/no-stock.error";
import {forkJoin, Observable, throwError} from "rxjs";
import {switchMap, tap} from "rxjs/operators";
import {CartQuantityNotifier} from "../domain/cart-quantity-notifier";
import {CartLine} from "../domain/dtos/cart-line";
import {CartLineMapper} from "../infrastructure/mappers/cart-line.mapper";
import {CartLineRepository} from "./cart-line-repository.service";
import {TrackSelectPromotionUseCase} from "@app/core/tracking/application/track-select-promotion.use-case";
import {Product} from "@app/core/product/domain/dtos/product";

/**
 * Add a promotion to the cart.
 */
@Injectable({
    providedIn: "root",
})
export class AddPromotionUseCase {
    constructor(
        private cartLineRepository: CartLineRepository,
        private cartController: CartQuantityNotifier,
        private checkStockUseCase: CheckStockUseCase,
        private trackSelectPromotionUseCase: TrackSelectPromotionUseCase,
    ) { }

    execute(promotion: PromotionDetail): Observable<CartLine[]> {
        const cartLines = CartLineMapper.fromPromotionToCartLines(promotion);

        return this.checkStockUseCase.execute(cartLines).pipe(
            switchMap(
                checkStockResult => {
                    // If all products have stock enough, add the promotion to the cart
                    if (!checkStockResult.withoutStock.length) {
                        this.trackSelectPromotion(promotion, checkStockResult.withStock);
                        // Build an array with the return value of each repository call
                        let observableList = cartLines.map(
                            cartLine => this.cartLineRepository.create(cartLine)
                        );

                        // Join the observable array in a single observable and notify
                        return forkJoin(observableList).pipe(
                            tap(() => this.cartController.notifyCartQuantityChanged())
                        );

                    } else {
                        // Throw a custom error so that it can be identified by the use case caller
                        const error = new NoStockError(checkStockResult.withoutStock);
                        return throwError(() => error)
                    }
                }
            ),
        );
    }

    /**
     * Track add promotion to cart event.
     * @param promotion - Promotion added to the cart.
     * @param products - Products added to the cart.
     */
    private trackSelectPromotion(promotion: PromotionDetail, products: Product[]) {
        this.trackSelectPromotionUseCase.execute({
            promotionId: promotion.code,
            promotionName: promotion.name,
            items: products.map(product => ({itemId: product.code, itemName: product.name})),
        })
    }
}
